import Phaser from 'phaser';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from '../render/layout';
import { makeTwoPassText, type TwoPassText } from '../render/twoPassText';
import { makePawButton } from '../render/standardButton';

interface InterstitialData {
  levelScore: number;
  levelNumber: number;
  levelPuppiesPlayed: number;
  levelMegaPuppies: number;
  levelMaxMegaPuppies: number;
  levelUndos: number;
  levelBonuses: number;
  bestLevelScore: number;
  mostPuppiesPlayed: number;
  mostMegaPuppies: number;
  mostMaxMegaPuppies: number;
  mostUndos: number;
  mostBonuses: number;
}

const DEFAULTS: InterstitialData = {
  levelScore: 0,
  levelNumber: 1,
  levelPuppiesPlayed: 0,
  levelMegaPuppies: 0,
  levelMaxMegaPuppies: 0,
  levelUndos: 0,
  levelBonuses: 0,
  bestLevelScore: 0,
  mostPuppiesPlayed: 0,
  mostMegaPuppies: 0,
  mostMaxMegaPuppies: 0,
  mostUndos: 0,
  mostBonuses: 0,
};

export class InterstitialScene extends Phaser.Scene {
  private initData: InterstitialData = DEFAULTS;
  private phase: 'goodJob' | 'scoreSheet' = 'goodJob';
  private goodJobLabels: TwoPassText[] = [];
  private goodJobImages: Phaser.GameObjects.GameObject[] = [];
  private fountain: Phaser.GameObjects.Particles.ParticleEmitter | null = null;

  constructor() {
    super({ key: 'Interstitial' });
  }

  init(data: Partial<InterstitialData>): void {
    this.initData = { ...DEFAULTS, ...data };
    this.phase = 'goodJob';
    this.goodJobLabels = [];
    this.goodJobImages = [];
    this.fountain = null;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#5fa84a');

    // ip6.png is the iOS interstitial background — sky, ground, trees baked in.
    this.add.image(0, 0, 'ip6').setOrigin(0, 0).setDisplaySize(LOGICAL_WIDTH, LOGICAL_HEIGHT);

    this.showGoodJob();

    // Tap to advance: first tap swaps the good-job message for the score
    // sheet; second tap (anywhere or the button) starts the next level.
    this.input.on('pointerdown', () => this.onPointerDown());
  }

  private showGoodJob(): void {
    const cx = LOGICAL_WIDTH / 2;
    const score = this.initData.levelScore.toLocaleString('en-US');

    // "Wow good job!" stays near the top.
    this.goodJobLabels.push(
      makeTwoPassText(this, cx, 220, 'Wow good job!', { fontSize: '96px' }),
    );

    // "Score: N,NNN" — full score text with its comma intact.
    const scoreY = Math.round(LOGICAL_HEIGHT / 2);
    const scoreLabel = makeTwoPassText(this, cx, scoreY, `Score: ${score}`, {
      fontSize: '96px',
      depth: 10,
    });
    this.goodJobLabels.push(scoreLabel);

    // smileylg sits at a fixed spot on the background (59% across, 31.7% down).
    const smileyX = Math.round(LOGICAL_WIDTH * 0.59);
    const smileyY = Math.round(LOGICAL_HEIGHT * 0.317);
    const smileyW = 70;
    const smiley = this.add
      .image(smileyX, smileyY, 'smileylg')
      .setOrigin(0.5)
      .setDisplaySize(smileyW, smileyW)
      .setDepth(20); // in front of the particle emitter
    this.goodJobImages.push(smiley);

    // Level X complete sits just below.
    this.goodJobLabels.push(
      makeTwoPassText(this, cx, scoreY + 130, `Level ${this.initData.levelNumber} complete`, {
        fontSize: '64px',
      }),
    );

    // "Get your score" paw-button — visual-only; scene-level pointerdown
    // handles the advance, so no hit-rect (interactive=false) to avoid
    // double-firing.
    const btnY = LOGICAL_HEIGHT - 110;
    const getScoreBtn = makePawButton({
      scene: this,
      x: cx,
      y: btnY,
      label: 'Get your score',
      fontSize: '40px',
      interactive: false,
      baseDepth: 10,
    });
    this.goodJobImages.push(...getScoreBtn.objects);

    // Smiley particle burst — emits in all directions from the smileylg
    // position for ~4 seconds, then stops; lingering particles fade out.
    this.fountain = this.add.particles(smileyX, smileyY, 'smiley', {
      speed: { min: 200, max: 600 },
      angle: { min: 0, max: 360 },
      lifespan: 1500,
      scale: { start: 1.0, end: 0.5 },
      rotate: { min: -180, max: 180 },
      alpha: { start: 1, end: 0 },
      quantity: 3,
      frequency: 30,
    });
    this.time.delayedCall(4000, () => {
      this.fountain?.stop();
    });
  }

  private onPointerDown(): void {
    if (this.phase === 'goodJob') {
      this.phase = 'scoreSheet';
      for (const lbl of this.goodJobLabels) lbl.destroy();
      this.goodJobLabels = [];
      for (const img of this.goodJobImages) img.destroy();
      this.goodJobImages = [];
      this.fountain?.destroy();
      this.fountain = null;
      this.showScoreSheet();
    } else {
      this.scene.start('Main', { advanceLevel: true });
    }
  }

  private showScoreSheet(): void {
    const d = this.initData;
    const cx = LOGICAL_WIDTH / 2;
    // Left edge for the rows; title is slightly above and uses a larger size.
    const leftX = cx - 320;
    let y = 140;

    makeTwoPassText(this, leftX, y, 'Score this Level:', {
      fontSize: '84px',
      originX: 0,
      originY: 0,
    });
    y += 130;

    const rowSize = '54px';
    const rowGap = 84;
    const rows = [
      `Puppies played: ${d.levelPuppiesPlayed} (Most: ${d.mostPuppiesPlayed})`,
      `Score: ${d.levelScore.toLocaleString('en-US')} (Best: ${d.bestLevelScore.toLocaleString('en-US')})`,
      `MegaPuppies: ${d.levelMegaPuppies} (Most: ${d.mostMegaPuppies})`,
      `Max MegaPuppies: ${d.levelMaxMegaPuppies} (Most: ${d.mostMaxMegaPuppies})`,
      `Number Undos: ${d.levelUndos} (Most: ${d.mostUndos})`,
      `Bonuses: ${d.levelBonuses} (Most: ${d.mostBonuses})`,
    ];
    for (const row of rows) {
      makeTwoPassText(this, leftX, y, row, { fontSize: rowSize, originX: 0, originY: 0 });
      y += rowGap;
    }

    // "Go to the next park!" paw-button — visual-only; scene-level
    // pointerdown handles the advance, so no hit-rect (interactive=false)
    // to avoid double-firing (which previously bumped the level by 2).
    makePawButton({
      scene: this,
      x: cx,
      y: LOGICAL_HEIGHT - 200,
      label: 'Go to the next park!',
      fontSize: '40px',
      interactive: false,
      baseDepth: 10,
    });
  }
}
